import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Box, Heading, Hide, Flex, ImageGallery } from '@qga/roo-ui/components';
import { useDataLayer } from 'hooks/useDataLayer';
import { setFullscreenGalleryContent } from 'store/ui/uiActions';
import theme from 'lib/theme';
import RoomTypeDisplayMeta from '../RoomTypeDisplayMeta';
import RoomOfferDetails from '../../../Offer/components/RoomOfferDetails';
import { FullWidthHeader } from '../primitives';
import { useBreakpoints } from 'hooks/useBreakpoints';
import Markup from 'components/Markup';
import Truncate from 'components/Truncate';

const RoomTypeDetailsGallery = ({ roomType, hasOffers, smallTablet = false, exclusiveOffer = false }) => {
  const dispatch = useDispatch();
  const { emitInteractionEvent } = useDataLayer();
  const { images, name, maxOccupantCount } = roomType;

  const { isLessThanBreakpoint } = useBreakpoints();
  const isMobile = isLessThanBreakpoint(0);
  const isNotDesktop = isLessThanBreakpoint(1);
  const roomImages = images || [];
  const hasImages = roomImages.length > 0;
  const thumbnailSize = theme.imageSizes.roomTypeThumbnail;
  const newThumbnailSize = theme.imageSizes.roomTypeThumbnail;
  const imageGalleryList = roomImages.map(({ urlLarge, urlSmall, caption }, index) => ({
    src: urlLarge,
    alt: caption,
    thumbnail: urlSmall,
    index: index + 1,
    total: roomImages.length,
  }));

  const handleOpenGallery = (startIndex) => {
    dispatch(setFullscreenGalleryContent({ images: imageGalleryList, startIndex }));
    emitInteractionEvent({ type: 'Room Type Gallery', value: 'Gallery Opened' });
  };

  const handleGalleryImageChanged = (id) => {
    emitInteractionEvent({ type: 'Room Type Gallery', value: `Gallery Scrolled to ${id}` });
  };

  const truncateLines = isMobile ? 3 : isNotDesktop ? 2 : 3;

  return (
    <>
      {isMobile && (
        <FullWidthHeader xs sm>
          <Heading.h3 fontSize="lg" mb={4}>
            {name}
          </Heading.h3>
        </FullWidthHeader>
      )}
      <Flex
        flexDirection={['column', 'row', 'column']}
        pl={smallTablet && !exclusiveOffer ? 0 : smallTablet && exclusiveOffer ? '40px' : !smallTablet ? [0, 8, 0] : 0}
        pr={smallTablet && exclusiveOffer ? '40px' : 0}
        pt={smallTablet && exclusiveOffer ? 8 : 0}
        height="auto"
      >
        {!isMobile && (
          <FullWidthHeader xs sm>
            <Heading.h3 fontSize="lg" mb={4}>
              {name}
            </Heading.h3>
          </FullWidthHeader>
        )}
        {!hasImages && (
          <Flex
            data-testid="no-image-box"
            height={newThumbnailSize.height}
            width={newThumbnailSize.width}
            minWidth={newThumbnailSize.width}
            borderRadius="default"
            bg="greys.porcelain"
            alignItems="center"
            justifyContent="center"
            color="greys.steel"
          >
            Image not available
          </Flex>
        )}
        {hasImages && (
          <Box
            position="relative"
            width={
              exclusiveOffer
                ? ['auto', '57.2%', '100%']
                : smallTablet && !exclusiveOffer
                  ? '59.7%'
                  : !smallTablet
                    ? newThumbnailSize.width
                    : thumbnailSize.width
            }
            data-testid="image-box"
            mb={4}
          >
            <ImageGallery
              images={imageGalleryList}
              onOpen={handleOpenGallery}
              onImageChanged={handleGalleryImageChanged}
              height={exclusiveOffer ? '180px' : newThumbnailSize.height}
              showFullscreenButton={true}
              showIndex={false}
              data-testid="image-gallery"
            />
          </Box>
        )}

        <Flex
          flex="1 1 auto"
          width={['auto', '60%', '100%']}
          height={isMobile ? 'auto' : [hasOffers ? 'thumbnailSize.height' : '90px', hasOffers ? newThumbnailSize.height : '90px', 'auto']}
          px={[0, 8, 0]}
          data-testid="room-description"
          alignItems="center"
        >
          <Box mb={[2, 0, 8]} px={[6, 0]}>
            <Hide md lg>
              <Heading.h3 fontSize="lg">{name}</Heading.h3>
            </Hide>
            <RoomTypeDisplayMeta maxOccupantCount={maxOccupantCount} />
            {!isMobile && !hasOffers && exclusiveOffer && <RoomOfferDetails roomType={roomType} />}
            {exclusiveOffer && isMobile && <RoomOfferDetails roomType={roomType} />}
            {hasOffers && (
              <Box mt={2} color="greys.steel" fontSize={isMobile || smallTablet ? '16px' : 'base'}>
                <Truncate lines={truncateLines}>
                  <Markup content={roomType.description} />
                </Truncate>
              </Box>
            )}
            {hasOffers && <RoomOfferDetails roomType={roomType} data-testid="room-offer-details" />}
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

RoomTypeDetailsGallery.propTypes = {
  roomType: PropTypes.shape({
    name: PropTypes.string,
    maxOccupantCount: PropTypes.number,
    images: PropTypes.arrayOf(PropTypes.object),
    roomTypeFacilities: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
    roomSize: PropTypes.string,
    bedConfiguration: PropTypes.string,
  }).isRequired,
  hasOffers: PropTypes.bool,
  smallTablet: PropTypes.bool,
  exclusiveOffer: PropTypes.bool,
};

export default RoomTypeDetailsGallery;
